import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import { collection, query, where, onSnapshot, orderBy, limit, Timestamp, Unsubscribe } from 'firebase/firestore'
import { db, auth } from '@/lib/firebase'
import { USE_MOCK } from '../config/env'
import { getFavorites } from '@/lib/favorites'
// Additional Firestore helpers if needed later

interface NotificationCounts {
  messages: number
  news: number
  groups: number
  marketplace: number
}

interface NotificationContextType {
  counts: NotificationCounts
  totalCount: number
  markMessagesRead: () => void
  markNewsRead: () => void
  markGroupsRead: () => void
  markMarketplaceRead: () => void
}

const NotificationContext = createContext<NotificationContextType | null>(null)

const LAST_SEEN_KEYS = {
  news: 'noe_last_seen_news',
  groups: 'noe_last_seen_groups',
  marketplace: 'noe_last_seen_marketplace',
}

function getTimestamp(key: string): Timestamp | null {
  const stored = localStorage.getItem(key)
  if (!stored) return null
  const ms = parseInt(stored, 10)
  if (isNaN(ms)) return null
  return Timestamp.fromMillis(ms)
}

function setTimestamp(key: string): void {
  localStorage.setItem(key, Date.now().toString())
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [counts, setCounts] = useState<NotificationCounts>({
    messages: 0,
    news: 0,
    groups: 0,
    marketplace: 0,
  })

  const markMessagesRead = useCallback(() => {
    setCounts(prev => ({ ...prev, messages: 0 }))
  }, [])

  const markNewsRead = useCallback(() => {
    setTimestamp(LAST_SEEN_KEYS.news)
    setCounts(prev => ({ ...prev, news: 0 }))
  }, [])

  const markGroupsRead = useCallback(() => {
    setTimestamp(LAST_SEEN_KEYS.groups)
    setCounts(prev => ({ ...prev, groups: 0 }))
  }, [])

  const markMarketplaceRead = useCallback(() => {
    setTimestamp(LAST_SEEN_KEYS.marketplace)
    setCounts(prev => ({ ...prev, marketplace: 0 }))
  }, [])

  useEffect(() => {
    if (USE_MOCK || !db) return

    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (!user) {
        setCounts({ messages: 0, news: 0, groups: 0, marketplace: 0 })
        return
      }

      const messagesQuery = query(
        collection(db, 'messages'),
        where('receiverId', '==', user.uid),
        where('read', '==', false)
      )

      const unsubMessages = onSnapshot(messagesQuery, (snapshot) => {
        setCounts(prev => ({ ...prev, messages: snapshot.size }))
      })

      const lastNewsSeen = getTimestamp(LAST_SEEN_KEYS.news)
      let newsQuery
      if (lastNewsSeen) {
        newsQuery = query(
          collection(db, 'news'),
          where('status', '==', 'published'),
          where('createdAt', '>', lastNewsSeen),
          orderBy('createdAt', 'desc'),
          limit(99)
        )
      } else {
        newsQuery = query(
          collection(db, 'news'),
          where('status', '==', 'published'),
          orderBy('createdAt', 'desc'),
          limit(1)
        )
      }

      const unsubNews = onSnapshot(newsQuery, (snapshot) => {
        setCounts(prev => ({ ...prev, news: snapshot.size }))
      }, () => {
        setCounts(prev => ({ ...prev, news: 0 }))
      })

      const lastGroupsSeen = getTimestamp(LAST_SEEN_KEYS.groups)
      let groupsQuery
      if (lastGroupsSeen) {
        groupsQuery = query(
          collection(db, 'groupPosts'),
          where('createdAt', '>', lastGroupsSeen),
          orderBy('createdAt', 'desc'),
          limit(99)
        )
      } else {
        groupsQuery = query(
          collection(db, 'groupPosts'),
          orderBy('createdAt', 'desc'),
          limit(1)
        )
      }

      const unsubGroups = onSnapshot(groupsQuery, (snapshot) => {
        setCounts(prev => ({ ...prev, groups: snapshot.size }))
      }, () => {
        setCounts(prev => ({ ...prev, groups: 0 }))
      })

      const lastMarketplaceSeen = getTimestamp(LAST_SEEN_KEYS.marketplace)
      let marketplaceQuery
      if (lastMarketplaceSeen) {
        marketplaceQuery = query(
          collection(db, 'listings'),
          where('status', '==', 'active'),
          where('approvalStatus', '==', 'approved'),
          where('createdAt', '>', lastMarketplaceSeen),
          orderBy('createdAt', 'desc'),
          limit(99)
        )
      } else {
        marketplaceQuery = query(
          collection(db, 'listings'),
          where('status', '==', 'active'),
          where('approvalStatus', '==', 'approved'),
          orderBy('createdAt', 'desc'),
          limit(1)
        )
      }

      const unsubMarketplace = onSnapshot(marketplaceQuery, (snapshot) => {
        setCounts(prev => ({ ...prev, marketplace: snapshot.size }))
      }, () => {
        setCounts(prev => ({ ...prev, marketplace: 0 }))
      })

      // Additional: watch favorites to tighten marketplace/group badges
      let unsubFavMarketplace: Unsubscribe | null = null
      let unsubFavGroups: Unsubscribe | null = null
      try {
        getFavorites(user.uid).then((fav) => {
          const favListingIds = (fav.listing ?? []).map((f) => f.itemId).filter((id) => !!id)
          const favGroupIds = (fav.group ?? []).map((f) => f.itemId).filter((id) => !!id)
          // marketplace from favorites
          if (favListingIds.length > 0) {
            const qFav = query(collection(db, 'listings'), where('id', 'in', favListingIds))
            unsubFavMarketplace = onSnapshot(qFav, (s) => {
              setCounts((p) => ({ ...p, marketplace: s.size }))
            })
          }
          // groups from favorites
          if (favGroupIds.length > 0) {
            const qFavG = query(collection(db, 'groupPosts'), where('groupId', 'in', favGroupIds))
            unsubFavGroups = onSnapshot(qFavG, (s) => {
              setCounts((p) => ({ ...p, groups: s.size }))
            })
          }
        })
      } catch (e) {
        // ignore extra watching errors in mock/dev
      }

      return () => {
        unsubMessages()
        unsubNews()
        unsubGroups()
        unsubMarketplace()
        if (unsubFavMarketplace) unsubFavMarketplace()
        if (unsubFavGroups) unsubFavGroups()
      }
    })

    return () => {
      unsubscribeAuth()
    }
  }, [])

  const totalCount = counts.messages + counts.news + counts.groups + counts.marketplace

  return (
    <NotificationContext.Provider
      value={{
        counts,
        totalCount,
        markMessagesRead,
        markNewsRead,
        markGroupsRead,
        markMarketplaceRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNotifications(): NotificationContextType {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}
